import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ClientAuthContext = createContext();

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within ClientAuthProvider');
  }
  return context;
};

export const ClientAuthProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('client_token'));
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/client/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setClient(response.data.client);
        } catch (error) {
          console.error('Token verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  const login = async (lastSixDigits, captchaAnswer = null, captchaId = null) => {
    try {
      const response = await axios.post(`${API}/client/auth/login`, {
        last_six_digits: lastSixDigits,
        captcha_answer: captchaAnswer,
        captcha_id: captchaId
      });

      const { token: newToken, client: clientData, needs_email } = response.data;
      
      setToken(newToken);
      setClient(clientData);
      localStorage.setItem('client_token', newToken);

      return { success: true, needs_email, client: clientData };
    } catch (error) {
      console.error('Login error:', error);
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || 'Échec de la connexion';
      
      return { 
        success: false, 
        error: status === 429 ? errorMsg : errorMsg,
        status: status
      };
    }
  };

  const updateEmail = async (email) => {
    try {
      await axios.patch(
        `${API}/client/profile/email`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setClient({ ...client, email });
      return { success: true };
    } catch (error) {
      console.error('Update email error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to update email' 
      };
    }
  };

  const logout = () => {
    setClient(null);
    setToken(null);
    localStorage.removeItem('client_token');
  };

  const getClientAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    client,
    token,
    loading,
    login,
    logout,
    updateEmail,
    getClientAuthHeader,
    isAuthenticated: !!token && !!client
  };

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
};
